import { Icon, InlineStack, SkeletonText, Text, Tooltip } from '@customerio/pluma-components/react';
import type { IconProps } from '@customerio/pluma-components/react';
import plur from 'plur';
import { TRIGGERED_BROADCAST_TYPE, useCollectionsUsage } from '@/services/db/collection/usage';

type UsageCountProps = {
  iconName: IconProps['name'];
  count: number;
};

function UsageCount({ iconName, count }: UsageCountProps) {
  return (
    <InlineStack gap="050" alignBlock="center">
      <Icon name={iconName} color="subtle" size="sm" />
      <Text size="sm">{count}</Text>
    </InlineStack>
  );
}

type CollectionUsageCellProps = {
  collectionId: string;
  workspaceId: number;
};

export function CollectionUsageCell({ collectionId, workspaceId }: CollectionUsageCellProps) {
  const { data: usage, isPending, isError } = useCollectionsUsage(workspaceId, collectionId);

  // Show a skeleton until this row's usage arrives so we don't flash a dash that becomes a count.
  if (isPending) {
    return <SkeletonText text="product-psm" style={{ width: '48px' }} />;
  }

  // A failed load must read differently from "unused" (a dash), so surface it as a warning
  // rather than under-reporting usage.
  if (isError) {
    return (
      <Tooltip content="Usage couldn't be loaded.">
        <Icon name="caution" color="subtle" size="sm" label="Usage couldn't be loaded" />
      </Tooltip>
    );
  }

  const campaigns = usage ?? [];
  const broadcastCount = campaigns.filter((campaign) => campaign.type === TRIGGERED_BROADCAST_TYPE).length;
  const campaignCount = campaigns.length - broadcastCount;

  if (campaigns.length === 0) {
    return <Text color="subtle">-</Text>;
  }

  const parts: string[] = [];
  if (campaignCount > 0) {
    const campaign = plur('automation', 'automations', campaignCount);
    parts.push(`${campaignCount} ${campaign}`);
  }
  if (broadcastCount > 0) {
    parts.push(`${broadcastCount} ${plur('API-triggered broadcast', broadcastCount)}`);
  }

  return (
    <Tooltip content={parts.join(' and ')}>
      <InlineStack gap="150" alignBlock="center">
        {campaignCount > 0 ? <UsageCount iconName="campaigns" count={campaignCount} /> : null}
        {broadcastCount > 0 ? <UsageCount iconName="api-triggered-broadcast" count={broadcastCount} /> : null}
      </InlineStack>
    </Tooltip>
  );
}
